import { useEffect, useRef, useState } from 'react';
import { BiSearch } from 'react-icons/bi';
import { MdCheck, MdKeyboardArrowDown } from 'react-icons/md';
import { useClickOutside } from '../../hooks/hooks';
import { LOADINGPRIMARY } from '../../assets/animated';

interface Props {
  value: string;
  onChange: (newValue: string) => void;
  placeholder: string;
  isSearchable?: boolean;
  size?: 'small' | 'base';
  callback: () => Promise<string[]>;
}

export function AsyncSelect({ value, onChange, placeholder, isSearchable, size, callback }: Props) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const dropDownRef = useRef(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      callback()
        .then((val) => {
          setOptions(val);
          setSearchResults(val);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [open]);

  const checkName = (name: string, str: string) => {
    var pattern = str
      .split('')
      .map((x) => {
        return `(?=.*${x})`;
      })
      .join('');
    var regex = new RegExp(`${pattern}`, 'g');
    return name.match(regex);
  };

  const handleSearch = (value: string) => {
    if (!value) {
      setSearchResults(options);
      return;
    }
    let temp = [...options];
    setSearchResults(
      temp.filter((item) => {
        return checkName(item, value) ? item : null;
      })
    );
  };

  useClickOutside(dropDownRef, () => setOpen(false));

  const padding = size === 'base' ? 'px-4 py-3' : size === 'small' ? 'p-2' : 'px-4 py-3';
  const fontSize = size === 'small' ? 'text-sm' : 'text-base';

  return (
    <div className="w-full">
      <div ref={dropDownRef} className="relative w-full">
        <div
          tabIndex={0}
          onClick={() => setOpen(!open)}
          className={`w-full flex items-center justify-between border-2 border-grey3 rounded-lg ${padding} cursor-pointer bg-white focus:border-neutralBlue1 focus:shadow-[0px_0px_0px_2px_rgb(182,207,255,0.25)]`}
        >
          <p className={`${fontSize} ${value ? 'text-black' : 'text-gray-400'} font-normal`}>
            {value ? value : placeholder}
          </p>
          <MdKeyboardArrowDown className={`text-base transition-all ${open ? 'rotate-180' : 'rotate-0'}`} />
        </div>
        {open && (
          <div
            className={`absolute top-[110%] left-0 z-40 w-full max-h-64 transition-all  border-2 border-grey3  overflow-y-scroll list-scrollbar rounded-lg py-2 drop-shadow-sm bg-white`}
          >
            {isSearchable && (
              <div className={`w-full flex items-center justify-between py-2 px-2 cursor-pointer rounded-md`}>
                <div className="flex items-center justify-between w-full px-4 py-2 border border-neutralBlue1 rounded-md">
                  <input
                    type="text"
                    placeholder="Search"
                    className="w-full text-sm placeholder:text-black/40 placeholder:font-light outline-none focus:outline-none"
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <BiSearch className="text-lg text-[#757D8A]" />
                </div>
              </div>
            )}
            {loading && (
              <div className="w-full my-4 flex items-center justify-center">
                <img src={LOADINGPRIMARY} className="w-6 h-6" />
              </div>
            )}
            {searchResults.map((option, index) => (
              <div
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                key={index}
                className={`w-full flex items-center justify-between py-2 px-4 cursor-pointer rounded-md ${
                  option === value ? 'bg-neutralBlue2' : 'bg-white'
                }`}
              >
                <p className="text-sm font-normal  capitalize">{option}</p>
                {option === value && <MdCheck className="text-primary text-base " />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
